import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MAINTENANCE_CATEGORY_ICONS } from './config/trackers';

/**
 * Maintenance Orders — custom Orders view (spec § 17).
 *
 * Replaces both the Accounts and Payments tabs. Shows status sub-tabs
 * (Open / In Progress / Completed) with counts, and groups orders by
 * fields.orderLocation. Clicking an order opens an inline order-detail
 * popup with quick actions (add quote, add note, add photo) that render
 * inline forms (no window.prompt or window.confirm calls).
 *
 * Photos are captured in-browser, downscaled + re-encoded to JPEG on a
 * canvas, and stored inline on the order as data URIs — there is no binary
 * upload endpoint for trackers, and inline storage keeps a photo readable
 * without a second authenticated request. Compression is what keeps the
 * Mongo document well clear of the 16MB cap; see PHOTO_STEPS.
 */

// Per-order photo budget. Each capture is squeezed through PHOTO_STEPS until
// it fits PHOTO_MAX_BYTES; the whole order stays a few MB at worst.
const MAX_PHOTOS = 8;
const PHOTO_MAX_BYTES = 700 * 1024;
const PHOTO_STEPS = [
    [1400, 0.72],
    [1100, 0.6],
    [900, 0.5],
    [700, 0.42],
];

const STATUS_TABS = [
    { id: 'open', icon: '📋', label: 'Open' },
    { id: 'inprogress', icon: '🔧', label: 'In Progress' },
    { id: 'completed', icon: '✓', label: 'Completed' },
];

function statusBucket(status) {
    if (status === 'Requested' || status === 'Assigned') return 'open';
    if (status === 'In Progress') return 'inprogress';
    if (status === 'Completed' || status === 'Cancelled') return 'completed';
    return 'open';
}

function priorityKey(priority) {
    if (!priority) return 'normal';
    const p = priority.toLowerCase();
    if (p.startsWith('urgent')) return 'urgent';
    if (p.startsWith('low')) return 'low';
    return 'normal';
}

function moneyToNumber(s) {
    const n = Number(String(s || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function nowStamp() {
    const d = new Date();
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatPretty(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(`${iso}T00:00`);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return iso;
    }
}

// Timeline entries are stored with a little inline markup (<strong> around
// vendor names), but their text also carries user input — notes are typed
// verbatim. Escape everything on the way out and re-enable <strong> only,
// so a note can never inject markup or script.
function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function renderUpdateHtml(text) {
    return escapeHtml(text)
        .replace(/&lt;strong&gt;/g, '<strong>')
        .replace(/&lt;\/strong&gt;/g, '</strong>');
}

// Inverse of the above, for pre-filling the edit box: drop tags, restore
// the handful of entities escapeHtml introduces.
function toPlainText(html) {
    return String(html == null ? '' : html)
        .replace(/<[^>]*>/g, '')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, '&');
}

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('Could not read that file'));
        reader.readAsDataURL(file);
    });
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not decode that image'));
        img.src = src;
    });
}

// Rough byte count of a base64 payload (4 chars ≈ 3 bytes).
function dataUrlBytes(dataUrl) {
    const i = String(dataUrl || '').indexOf(',');
    if (i === -1) return 0;
    return Math.round((dataUrl.length - i - 1) * 0.75);
}

function drawToDataUrl(img, maxDim, quality) {
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // JPEG has no alpha — paint white first so transparent PNGs don't go black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return { dataUrl: canvas.toDataURL('image/jpeg', quality), width: w, height: h };
}

/**
 * Downscale/re-encode until the result fits PHOTO_MAX_BYTES. Returns the
 * smallest attempt if even the last step is too big — the caller decides
 * whether to accept it.
 */
async function compressImage(file) {
    const raw = await readFileAsDataUrl(file);
    const img = await loadImage(raw);
    let last = null;
    for (let i = 0; i < PHOTO_STEPS.length; i += 1) {
        const [dim, q] = PHOTO_STEPS[i];
        last = drawToDataUrl(img, dim, q);
        last.bytes = dataUrlBytes(last.dataUrl);
        if (last.bytes <= PHOTO_MAX_BYTES) return last;
    }
    return last;
}

function lowestQuoteAmount(quotes) {
    const arr = (quotes || [])
        .map((q) => moneyToNumber(q.amount))
        .filter((n) => n > 0);
    if (arr.length === 0) return 0;
    return Math.min(...arr);
}

/**
 * Order-detail actions touch one or two keys at a time, so they send
 * `fieldsPatch` — the server merges it into the stored map. That keeps
 * inline photo data URIs out of every unrelated save (adding a note used to
 * round-trip the whole photo set). months/subTaxes are omitted for the same
 * reason: the server preserves them when the payload doesn't carry them.
 */
function buildPayload(order, patch) {
    return {
        accountId: order.accountId,
        trackerKey: 'maintenance',
        label: patch.label != null ? patch.label : order.label,
        belongs: patch.belongs != null ? patch.belongs : order.belongs,
        frequency: 'asNeeded',
        fieldsPatch: patch.fields || {},
        paymentMethod: order.paymentMethod,
    };
}

function MaintenanceView({ accounts, api, showToast, onEditOrder }) {
    const [tab, setTab] = useState('open');
    const [openLoc, setOpenLoc] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const orders = useMemo(() => accounts || [], [accounts]);

    const counts = useMemo(() => {
        const c = { open: 0, inprogress: 0, completed: 0 };
        orders.forEach((o) => {
            const b = statusBucket(o.fields?.orderStatus || 'Requested');
            c[b] += 1;
        });
        return c;
    }, [orders]);

    const filtered = useMemo(
        () => orders.filter(
            (o) => statusBucket(o.fields?.orderStatus || 'Requested') === tab,
        ),
        [orders, tab],
    );

    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((o) => {
            const key = o.fields?.orderLocation || '(no location)';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(o);
        });
        const out = [];
        Array.from(map.entries()).forEach(([loc, list]) => {
            const sorted = [...list].sort((a, b) => {
                const pa = priorityKey(a.fields?.priority);
                const pb = priorityKey(b.fields?.priority);
                const score = (k) => (k === 'urgent' ? 0 : k === 'normal' ? 1 : 2);
                const diff = score(pa) - score(pb);
                if (diff !== 0) return diff;
                return (b.fields?.dateRequested || '').localeCompare(
                    a.fields?.dateRequested || '',
                );
            });
            out.push({ loc, orders: sorted });
        });
        out.sort((a, b) => a.loc.localeCompare(b.loc));
        return out;
    }, [filtered]);

    const refreshDetail = (accountId) => {
        const next = orders.find((o) => o.accountId === accountId);
        if (next) setDetailTarget(next);
    };

    // Soft-delete: the row is retained server-side for audit but drops out of
    // every list response, so there is no in-app restore — hence the confirm.
    const handleDeleteOrder = () => {
        if (!deleteTarget || deleting) return;
        setDeleting(true);
        api.deleteAccount(
            deleteTarget.accountId,
            (err) => {
                setDeleting(false);
                if (!err) {
                    showToast('Order deleted', 'success');
                    setDeleteTarget(null);
                    setDetailTarget(null);
                }
            },
            { silent: true },
        );
    };

    return (
        <>
            {/* Status sub-tabs */}
            <div className="bt-mtnce-tabs">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`bt-mtnce-tab ${tab === t.id ? 'active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        <span className="bt-mtnce-tab-icon" role="img" aria-hidden="true">
                            {t.icon}
                        </span>
                        {t.label}
                        <span className="bt-mtnce-tab-count">{counts[t.id]}</span>
                    </button>
                ))}
            </div>

            {groups.length === 0 ? (
                <div className="bt-empty">
                    No {tab === 'open' ? 'open' : tab === 'inprogress' ? 'in-progress' : 'completed'} orders.
                </div>
            ) : (
                <div className="bt-loc-list">
                    {groups.map((g) => (
                        <OrderLocationCard
                            key={g.loc}
                            group={g}
                            open={openLoc === g.loc}
                            onToggle={() => setOpenLoc(openLoc === g.loc ? null : g.loc)}
                            onOrderClick={(o) => setDetailTarget(o)}
                            onEditOrder={onEditOrder}
                            onDeleteOrder={(o) => setDeleteTarget(o)}
                        />
                    ))}
                </div>
            )}

            {detailTarget && (
                <OrderDetailPopup
                    key={detailTarget.accountId}
                    order={
                        orders.find((o) => o.accountId === detailTarget.accountId)
                            || detailTarget
                    }
                    onClose={() => setDetailTarget(null)}
                    api={api}
                    showToast={showToast}
                    onRefresh={refreshDetail}
                />
            )}

            {deleteTarget && (
                <div
                    className="bt-overlay bt-overlay--center"
                    role="presentation"
                    onClick={() => !deleting && setDeleteTarget(null)}
                >
                    <div
                        className="bt-modal"
                        role="dialog"
                        aria-modal="true"
                        style={{ maxWidth: 420 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bt-order-danger">
                            <div className="bt-order-danger-title">Delete this order?</div>
                            <div className="bt-order-danger-text">
                                <strong>{deleteTarget.label || '(untitled order)'}</strong>{' '}
                                and its quotes, photos, and history will be removed from
                                Maintenance Orders. The record is archived server-side for
                                audit, but it can&apos;t be restored from here.
                            </div>
                            <div className="bt-inline-form-actions">
                                <button
                                    type="button"
                                    className="bt-btn bt-btn-ghost"
                                    onClick={() => setDeleteTarget(null)}
                                    disabled={deleting}
                                >
                                    Keep
                                </button>
                                <button
                                    type="button"
                                    className="bt-btn bt-btn-danger"
                                    onClick={handleDeleteOrder}
                                    disabled={deleting}
                                >
                                    🗑 Delete Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function OrderLocationCard({ group, open, onToggle, onOrderClick, onEditOrder, onDeleteOrder }) {
    const urgentCount = group.orders.filter(
        (o) => priorityKey(o.fields?.priority) === 'urgent',
    ).length;
    const count = group.orders.length;

    return (
        <div
            className={`bt-ordloc${open ? ' expanded' : ''}${urgentCount > 0 ? ' urgent' : ''}`}
        >
            <div
                className="bt-ordloc-head"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onToggle()}
            >
                <div className="bt-ordloc-info">
                    <div className="bt-ordloc-name">{group.loc}</div>
                    <div className="bt-ordloc-meta">
                        <span>{count} order{count === 1 ? '' : 's'}</span>
                        {urgentCount > 0 && (
                            <span className="bt-ordloc-urgent">
                                <span className="bt-ordloc-dot" aria-hidden="true" />
                                {urgentCount} urgent
                            </span>
                        )}
                    </div>
                </div>
                <div className="bt-ordloc-count">{count}</div>
                <div className="bt-ordloc-chevron" aria-hidden="true">⌄</div>
            </div>

            {open && (
                <div className="bt-ordloc-body">
                    <div className="bt-order-list">
                        {group.orders.map((o, i) => (
                            <OrderRow
                                key={o.accountId}
                                order={o}
                                index={i}
                                onClick={() => onOrderClick(o)}
                                onEdit={onEditOrder}
                                onDelete={onDeleteOrder}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function OrderRow({ order, index, onClick, onEdit, onDelete }) {
    const f = order.fields || {};
    const status = f.orderStatus || 'Requested';
    const pKey = priorityKey(f.priority);
    const category = f.category || 'General';
    const catIcon = MAINTENANCE_CATEGORY_ICONS[category] || '🔧';
    const quotes = Array.isArray(f.quotes) ? f.quotes : [];
    const photos = Array.isArray(f.photos) ? f.photos : [];

    const statusKey = status.toLowerCase().replace(/ /g, '');

    let costLabel = '—';
    if (status === 'Completed' && f.actualCost) {
        costLabel = `${f.actualCost} actual`;
    } else if (quotes.length > 0) {
        const low = lowestQuoteAmount(quotes);
        if (low > 0) costLabel = `$${low.toLocaleString()} quoted`;
        else if (f.quotedCost) costLabel = `${f.quotedCost} quoted`;
    } else if (f.quotedCost) {
        costLabel = `${f.quotedCost} quoted`;
    }

    let quotePill = null;
    if (status === 'Requested' && quotes.length === 0) {
        quotePill = (
            <span className="bt-order-pill noquote">
                <span role="img" aria-label="Money">💰</span> No quotes yet
            </span>
        );
    } else if (status === 'Requested' && quotes.length > 1) {
        quotePill = (
            <span className="bt-order-pill choose">
                <span role="img" aria-label="Money">💰</span> {quotes.length} quotes · choose
            </span>
        );
    } else if (quotes.length > 0) {
        quotePill = (
            <span className="bt-order-pill quotes">
                <span role="img" aria-label="Money">💰</span> {quotes.length} quote{quotes.length === 1 ? '' : 's'}
            </span>
        );
    }

    return (
        <div
            className={`bt-order-row status-${statusKey}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className="bt-order-num">#{index + 1}</div>
            <div className="bt-order-icon" role="img" aria-label={category}>{catIcon}</div>
            <div className="bt-order-info">
                <div className="bt-order-title">{order.label || '(untitled)'}</div>
                <div className="bt-order-pills">
                    <span className={`bt-order-pill priority-${pKey}`}>
                        {pKey === 'urgent' ? 'Urgent' : pKey === 'low' ? 'Low' : 'Normal'}
                    </span>
                    <span className="bt-order-pill cat">{category}</span>
                    {quotePill}
                    {photos.length > 0 && (
                        <span className="bt-order-pill photos">
                            <span role="img" aria-label="Photos">📷</span> {photos.length}
                        </span>
                    )}
                </div>
                <div className="bt-order-sub">
                    <span><span role="img" aria-label="Vendor">👷</span> {f.vendor || 'TBD'}</span>
                    <span>·</span>
                    <span><span role="img" aria-label="Date">📅</span> {formatPretty(f.dateRequested)}</span>
                </div>
            </div>
            <div className="bt-order-right">
                <div className={`bt-order-status-badge status-${statusKey}`}>{status}</div>
                <div className="bt-order-cost">{costLabel}</div>
                {/* Row-level edit/delete. Both stop propagation so they never
                    open the order popup underneath them. */}
                <div className="bt-order-actions">
                    {onEdit && (
                        <button
                            type="button"
                            className="bt-order-act"
                            title="Edit order"
                            aria-label={`Edit ${order.label || 'order'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(order);
                            }}
                        >
                            ✎
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            className="bt-order-act danger"
                            title="Delete order"
                            aria-label={`Delete ${order.label || 'order'}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(order);
                            }}
                        >
                            🗑
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function OrderDetailPopup({ order, onClose, api, showToast, onRefresh }) {
    const [activeAction, setActiveAction] = useState(null);
    const [saving, setSaving] = useState(false);
    // Timeline editing state — index into fields.updates (true index, not the
    // reversed display order) plus the in-flight text.
    const [editingUpdate, setEditingUpdate] = useState(null);
    const [confirmUpdate, setConfirmUpdate] = useState(null);
    const [lightbox, setLightbox] = useState(null);

    const f = order.fields || {};
    const status = f.orderStatus || 'Requested';
    const statusKey = status.toLowerCase().replace(/ /g, '');
    const quotes = Array.isArray(f.quotes) ? f.quotes : [];
    const photos = Array.isArray(f.photos) ? f.photos : [];
    const updates = Array.isArray(f.updates) ? f.updates : [];

    const isClosed = status === 'Completed' || status === 'Cancelled';
    const category = f.category || 'General';

    // `silent` suppresses the generic "Account updated" toast from the api
    // wrapper so each action can report itself ("Quote added") exactly once.
    const persist = (patch, msg) => {
        if (saving) return;
        setSaving(true);
        const payload = buildPayload(order, patch);
        api.saveAccount(
            payload,
            (err) => {
                setSaving(false);
                if (!err) {
                    if (msg) showToast(msg, 'success');
                    onRefresh(order.accountId);
                }
            },
            { silent: true },
        );
    };

    const appendUpdate = (entry) => ({
        ...entry,
        date: nowStamp(),
    });

    const handleAddQuote = (vendor, amount, notes) => {
        const newQuote = {
            vendor: (vendor || '').trim(),
            amount: (amount || '').trim(),
            notes: (notes || '').trim(),
            date: nowStamp(),
            assigned: false,
        };
        const newQuotes = [...quotes, newQuote];
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '💰',
                type: 'Quote received',
                text: `<strong>${newQuote.vendor || 'Vendor'}</strong> quoted ${newQuote.amount || '—'}`,
            }),
        ];
        const lowest = lowestQuoteAmount(newQuotes);
        const fields = {
            quotes: newQuotes,
            updates: newUpdates,
        };
        if (status === 'Requested' && lowest > 0) {
            fields.quotedCost = `$${lowest.toLocaleString()}`;
        }
        persist({ fields }, 'Quote added');
        setActiveAction(null);
    };

    const handleAddNote = (text) => {
        const trimmed = (text || '').trim();
        if (!trimmed) return;
        const newUpdates = [
            ...updates,
            appendUpdate({ icon: '📝', type: 'Note added', text: trimmed }),
        ];
        persist({ fields: { updates: newUpdates } }, 'Note added');
        setActiveAction(null);
    };

    /**
     * photo: { dataUrl, width, height, bytes } from compressImage(), or null
     * when the user only recorded a description (the pre-upload behaviour).
     */
    const handleAddPhoto = (description, photo) => {
        const desc = (description || '').trim();
        if (!desc) return;
        const entry = { name: desc, date: nowStamp() };
        if (photo && photo.dataUrl) {
            entry.dataUrl = photo.dataUrl;
            entry.width = photo.width;
            entry.height = photo.height;
            entry.bytes = photo.bytes;
        }
        const newPhotos = [...photos, entry];
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '📷',
                type: entry.dataUrl ? 'Photo added' : 'Photo entry added',
                text: desc,
            }),
        ];
        persist(
            { fields: { photos: newPhotos, updates: newUpdates } },
            entry.dataUrl ? 'Photo added' : 'Photo entry added',
        );
        setActiveAction(null);
    };

    const handleChooseVendor = (idx) => {
        const chosen = quotes[idx];
        if (!chosen) return;
        const newQuotes = quotes.map((q, i) => ({ ...q, assigned: i === idx }));
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '✓',
                type: 'Vendor assigned',
                text: `<strong>${chosen.vendor || 'Vendor'}</strong> chosen at ${chosen.amount || '—'}`,
            }),
        ];
        persist(
            {
                fields: {
                    quotes: newQuotes,
                    vendor: chosen.vendor,
                    quotedCost: chosen.amount,
                    orderStatus: 'Assigned',
                    updates: newUpdates,
                },
            },
            'Vendor assigned',
        );
    };

    const handleRemoveQuote = (idx) => {
        const removed = quotes[idx];
        const newQuotes = quotes.filter((_, i) => i !== idx);
        const fields = { quotes: newQuotes };
        // Dropping the chosen quote has to undo the assignment too, otherwise
        // the order keeps a vendor and an "Assigned" status sourced from a
        // quote that no longer exists.
        if (removed && removed.assigned) {
            fields.vendor = 'TBD';
            fields.quotedCost = '';
            if (status === 'Assigned') fields.orderStatus = 'Requested';
            fields.updates = [
                ...updates,
                appendUpdate({
                    icon: '↩',
                    type: 'Vendor unassigned',
                    text: `<strong>${removed.vendor || 'Vendor'}</strong> removed — order back to Requested`,
                }),
            ];
        }
        persist({ fields }, 'Quote removed');
    };

    const handleRemovePhoto = (idx) => {
        const removed = photos[idx];
        const newPhotos = photos.filter((_, i) => i !== idx);
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '🗑',
                type: 'Photo removed',
                text: removed?.name || 'Photo removed',
            }),
        ];
        persist({ fields: { photos: newPhotos, updates: newUpdates } }, 'Photo removed');
    };

    const handleStartWork = () => {
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '🔧',
                type: 'Work started',
                text: 'Order moved to In Progress',
            }),
        ];
        persist(
            { fields: { orderStatus: 'In Progress', updates: newUpdates } },
            'Work started',
        );
    };

    // ---- Updates & History editing -----------------------------------
    // Entries are addressed by their true index in fields.updates; the
    // timeline renders newest-first, so the display order is reversed
    // separately and never used as the identity.

    const handleEditUpdate = (idx, text) => {
        const trimmed = (text || '').trim();
        if (!trimmed) return;
        const newUpdates = updates.map((u, i) => (
            i === idx ? { ...u, text: trimmed, edited: nowStamp() } : u
        ));
        setEditingUpdate(null);
        persist({ fields: { updates: newUpdates } }, 'Entry updated');
    };

    const handleDeleteUpdate = (idx) => {
        const newUpdates = updates.filter((_, i) => i !== idx);
        setConfirmUpdate(null);
        setEditingUpdate(null);
        persist({ fields: { updates: newUpdates } }, 'Entry deleted');
    };

    const handleMarkCompleted = (cost) => {
        const final = (cost || '').trim();
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '✓',
                type: 'Order completed',
                text: `Final cost: <strong>${final || '—'}</strong>`,
            }),
        ];
        persist(
            {
                fields: {
                    orderStatus: 'Completed',
                    actualCost: final,
                    dateCompleted: todayIso(),
                    updates: newUpdates,
                },
            },
            'Order completed',
        );
        setActiveAction(null);
    };

    const handleAssignScroll = () => {
        setActiveAction(null);
        const el = document.getElementById('bt-quotes-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const pickedQuote = quotes.find((q) => q.assigned);

    return (
        <div className="bt-overlay" onClick={onClose}>
            <div
                className="bt-modal bt-order-modal"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-modal-head">
                    <h2>
                        <span role="img" aria-label={category}>
                            {MAINTENANCE_CATEGORY_ICONS[category] || '🔧'}
                        </span>{' '}
                        {order.label || '(untitled order)'}
                    </h2>
                    <button
                        className="bt-modal-close"
                        onClick={onClose}
                        type="button"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>
                <div className="bt-order-detail-sub">
                    {f.orderLocation || '—'}
                    {category ? ` — ${category}` : ''}
                </div>
                <div className={`bt-order-detail-badge status-${statusKey}`}>{status}</div>

                {f.description && (
                    <div className="bt-order-desc">{f.description}</div>
                )}

                <div className="bt-detail-grid">
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Priority</div>
                        <div className="bt-detail-value">{f.priority || 'Normal'}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Assigned Vendor</div>
                        <div className="bt-detail-value">
                            {pickedQuote?.vendor || (f.vendor && f.vendor !== 'TBD' ? f.vendor : '— Not yet assigned —')}
                        </div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Date Requested</div>
                        <div className="bt-detail-value">{formatPretty(f.dateRequested)}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Quotes Received</div>
                        <div className="bt-detail-value">{quotes.length}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Final Cost</div>
                        <div className="bt-detail-value">
                            {f.actualCost && f.actualCost !== '$0' ? f.actualCost : '—'}
                        </div>
                    </div>
                </div>

                {!isClosed && (
                    <div className="bt-modal-section">
                        <div className="bt-modal-section-label">
                            <span role="img" aria-label="Quick">⚡</span> Quick Actions
                        </div>
                        <div className="bt-quick-actions">
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'quote' ? null : 'quote')}
                            >
                                <span role="img" aria-label="Quote">💰</span> Add Quote
                            </button>
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'note' ? null : 'note')}
                            >
                                <span role="img" aria-label="Note">📝</span> Add Note
                            </button>
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'photo' ? null : 'photo')}
                            >
                                <span role="img" aria-label="Photo">📷</span> Add Photo
                            </button>
                        </div>
                        {activeAction === 'quote' && (
                            <AddQuoteForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddQuote}
                            />
                        )}
                        {activeAction === 'note' && (
                            <AddNoteForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddNote}
                            />
                        )}
                        {activeAction === 'photo' && (
                            <AddPhotoForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddPhoto}
                                remainingSlots={MAX_PHOTOS - photos.length}
                            />
                        )}
                        {activeAction === 'complete' && (
                            <CompleteForm
                                defaultValue={f.quotedCost || ''}
                                onCancel={() => setActiveAction(null)}
                                onSave={handleMarkCompleted}
                            />
                        )}
                    </div>
                )}

                <div className="bt-modal-section" id="bt-quotes-section">
                    <div className="bt-modal-section-label">
                        <span role="img" aria-label="Quotes">💰</span> Quotes ({quotes.length})
                    </div>
                    {quotes.length === 0 ? (
                        <div className="bt-quotes-empty">No quotes yet.</div>
                    ) : (
                        <div className="bt-quotes-list">
                            {quotes.map((q, i) => (
                                <div key={i} className={`bt-quote-card${q.assigned ? ' picked' : ''}`}>
                                    <div className="bt-quote-head">
                                        <div className="bt-quote-vendor">{q.vendor || '—'}</div>
                                        <div className="bt-quote-amount">{q.amount || '—'}</div>
                                    </div>
                                    {q.notes && <div className="bt-quote-notes">{q.notes}</div>}
                                    <div className="bt-quote-foot">
                                        <span className="bt-quote-date">{q.date || ''}</span>
                                        {q.assigned ? (
                                            <span className="bt-quote-picked">
                                                <span role="img" aria-label="Selected">✓</span> Selected vendor
                                            </span>
                                        ) : !isClosed ? (
                                            <span className="bt-quote-actions">
                                                <button
                                                    type="button"
                                                    className="bt-quote-btn pick"
                                                    onClick={() => handleChooseVendor(i)}
                                                >
                                                    Choose this vendor
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bt-quote-btn remove"
                                                    onClick={() => handleRemoveQuote(i)}
                                                >
                                                    Remove
                                                </button>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {photos.length > 0 && (
                    <div className="bt-modal-section">
                        <div className="bt-modal-section-label">
                            <span role="img" aria-label="Photos">📷</span> Photos ({photos.length})
                        </div>
                        <div className="bt-photo-grid">
                            {photos.map((p, i) => (
                                <div key={i} className="bt-photo-tile">
                                    {p.dataUrl ? (
                                        <button
                                            type="button"
                                            className="bt-photo-shot"
                                            onClick={() => setLightbox(p)}
                                            aria-label={`View ${p.name || 'photo'}`}
                                        >
                                            <img src={p.dataUrl} alt={p.name || 'Order photo'} />
                                        </button>
                                    ) : (
                                        <div className="bt-photo-shot placeholder">
                                            <span role="img" aria-label="No image">🖼</span>
                                            <span className="bt-photo-noimg">No image</span>
                                        </div>
                                    )}
                                    <div className="bt-photo-meta">
                                        <div className="bt-photo-name" title={p.name}>{p.name}</div>
                                        <div className="bt-photo-date">{p.date}</div>
                                    </div>
                                    {!isClosed && (
                                        <button
                                            type="button"
                                            className="bt-photo-remove"
                                            onClick={() => handleRemovePhoto(i)}
                                            aria-label={`Remove ${p.name || 'photo'}`}
                                            title="Remove photo"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">
                        <span role="img" aria-label="Updates">📋</span> Updates &amp; History
                    </div>
                    {updates.length === 0 ? (
                        <div className="bt-quotes-empty">No updates yet.</div>
                    ) : (
                        <div className="bt-timeline">
                            {/* Newest first for display, but every action is keyed
                                by the entry's true index in fields.updates. */}
                            {updates
                                .map((u, idx) => ({ u, idx }))
                                .reverse()
                                .map(({ u, idx }) => (
                                    <div key={idx} className="bt-timeline-item">
                                        <div className="bt-timeline-icon" role="img" aria-label="Update">
                                            {u.icon || '•'}
                                        </div>
                                        <div className="bt-timeline-content">
                                            <div className="bt-timeline-head">
                                                <div className="bt-timeline-type">{u.type || 'Update'}</div>
                                                <div className="bt-timeline-tools">
                                                    <button
                                                        type="button"
                                                        className="bt-timeline-tool"
                                                        title="Edit entry"
                                                        aria-label="Edit entry"
                                                        onClick={() => {
                                                            setConfirmUpdate(null);
                                                            setEditingUpdate({
                                                                idx,
                                                                text: toPlainText(u.text),
                                                            });
                                                        }}
                                                    >
                                                        ✎
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bt-timeline-tool danger"
                                                        title="Delete entry"
                                                        aria-label="Delete entry"
                                                        onClick={() => {
                                                            setEditingUpdate(null);
                                                            setConfirmUpdate(idx);
                                                        }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            </div>

                                            {editingUpdate && editingUpdate.idx === idx ? (
                                                <div className="bt-timeline-edit">
                                                    <textarea
                                                        className="bt-field-input"
                                                        rows={3}
                                                        value={editingUpdate.text}
                                                        onChange={(e) => setEditingUpdate({
                                                            idx,
                                                            text: e.target.value,
                                                        })}
                                                    />
                                                    <div className="bt-inline-form-actions">
                                                        <button
                                                            type="button"
                                                            className="bt-btn bt-btn-ghost"
                                                            onClick={() => setEditingUpdate(null)}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="bt-btn bt-btn-save"
                                                            disabled={!editingUpdate.text.trim()}
                                                            onClick={() => handleEditUpdate(idx, editingUpdate.text)}
                                                        >
                                                            Save Entry
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className="bt-timeline-text"
                                                    // Escaped in renderUpdateHtml — only <strong> survives.
                                                    // eslint-disable-next-line react/no-danger
                                                    dangerouslySetInnerHTML={{
                                                        __html: renderUpdateHtml(u.text || ''),
                                                    }}
                                                />
                                            )}

                                            {confirmUpdate === idx && (
                                                <div className="bt-timeline-confirm">
                                                    <span>Delete this entry?</span>
                                                    <button
                                                        type="button"
                                                        className="bt-quote-btn"
                                                        onClick={() => setConfirmUpdate(null)}
                                                    >
                                                        Keep
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="bt-quote-btn remove"
                                                        onClick={() => handleDeleteUpdate(idx)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}

                                            <div className="bt-timeline-date">
                                                {u.date || ''}
                                                {u.edited ? ` · edited ${u.edited}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                <div className="bt-modal-actions">
                    <button
                        className="bt-btn bt-btn-ghost"
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                    {status === 'Requested' && quotes.length === 0 && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={() => setActiveAction('quote')}
                            type="button"
                        >
                            + Add First Quote
                        </button>
                    )}
                    {status === 'Requested' && quotes.length > 0 && !pickedQuote && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={handleAssignScroll}
                            type="button"
                        >
                            → Assign Vendor
                        </button>
                    )}
                    {status === 'Assigned' && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={handleStartWork}
                            type="button"
                        >
                            → Start Work
                        </button>
                    )}
                    {status === 'In Progress' && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={() => setActiveAction('complete')}
                            type="button"
                        >
                            → Mark Completed
                        </button>
                    )}
                </div>
            </div>

            {lightbox && (
                <PhotoLightbox photo={lightbox} onClose={() => setLightbox(null)} />
            )}
        </div>
    );
}

/** Full-size photo viewer. Click anywhere (or Esc) to dismiss. */
function PhotoLightbox({ photo, onClose }) {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className="bt-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={photo.name || 'Photo'}
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <button
                type="button"
                className="bt-lightbox-close"
                onClick={onClose}
                aria-label="Close photo"
            >
                ✕
            </button>
            <img src={photo.dataUrl} alt={photo.name || 'Order photo'} />
            <div className="bt-lightbox-cap">
                <span>{photo.name}</span>
                <span className="bt-lightbox-sub">
                    {photo.date}
                    {photo.width ? ` · ${photo.width}×${photo.height}` : ''}
                    {photo.bytes ? ` · ${Math.round(photo.bytes / 1024)} KB` : ''}
                </span>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------
// Inline forms — replace window.prompt usage. Each renders into the
// "Quick Actions" section of the order detail popup.
// ----------------------------------------------------------------------

function AddQuoteForm({ onCancel, onSave }) {
    const [vendor, setVendor] = useState('');
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState({});
    const submit = () => {
        const next = {};
        if (!vendor.trim()) next.vendor = 'Required';
        if (!amount.trim()) next.amount = 'Required';
        setErrors(next);
        if (Object.keys(next).length > 0) return;
        onSave(vendor, amount, notes);
    };
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add a quote</div>
            <div className="bt-field">
                <label className="bt-field-label">Vendor *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="e.g. CoolTech HVAC"
                />
                {errors.vendor && <div className="bt-field-error">{errors.vendor}</div>}
            </div>
            <div className="bt-field">
                <label className="bt-field-label">Amount *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. $850"
                />
                {errors.amount && <div className="bt-field-error">{errors.amount}</div>}
            </div>
            <div className="bt-field">
                <label className="bt-field-label">Notes</label>
                <textarea
                    className="bt-field-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional details about this quote"
                    rows={2}
                />
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="bt-btn bt-btn-save" onClick={submit}>
                    Save Quote
                </button>
            </div>
        </div>
    );
}

function AddNoteForm({ onCancel, onSave }) {
    const [text, setText] = useState('');
    const [error, setError] = useState('');
    const submit = () => {
        if (!text.trim()) {
            setError('Required');
            return;
        }
        onSave(text);
    };
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add a note</div>
            <div className="bt-field">
                <label className="bt-field-label">Note *</label>
                <textarea
                    className="bt-field-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Add an update, follow-up, or comment..."
                    rows={3}
                />
                {error && <div className="bt-field-error">{error}</div>}
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="bt-btn bt-btn-save" onClick={submit}>
                    Save Note
                </button>
            </div>
        </div>
    );
}

/**
 * Photo capture: pick (or shoot, on mobile) an image, preview it, and pair
 * it with a description. The file never leaves the browser un-shrunk — it's
 * redrawn on a canvas and stored as a compressed JPEG data URI on the order.
 * A description alone is still allowed, so the pre-upload flow keeps working.
 */
function AddPhotoForm({ onCancel, onSave, remainingSlots }) {
    const [desc, setDesc] = useState('');
    const [photo, setPhoto] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const full = remainingSlots <= 0;

    const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        // Let the same file be re-picked after a remove.
        e.target.value = '';
        if (!file) return;
        if (!/^image\//.test(file.type)) {
            setError('That file is not an image.');
            return;
        }
        setError('');
        setBusy(true);
        try {
            const out = await compressImage(file);
            if (!out || !out.dataUrl) throw new Error('Could not process that image');
            if (out.bytes > PHOTO_MAX_BYTES * 2) {
                setError('That image is too large even after compression — try a smaller one.');
                setPhoto(null);
            } else {
                setPhoto(out);
                // Give the description a sensible starting point.
                if (!desc.trim()) {
                    setDesc(file.name.replace(/\.[^.]+$/, '').slice(0, 80));
                }
            }
        } catch (err) {
            setError(err.message || 'Could not read that image');
            setPhoto(null);
        } finally {
            setBusy(false);
        }
    };

    const submit = () => {
        if (!desc.trim()) {
            setError('A description is required.');
            return;
        }
        onSave(desc, photo);
    };

    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add photo</div>

            {full ? (
                <div className="bt-field-hint">
                    This order already has the maximum of {MAX_PHOTOS} photos. Remove
                    one before adding another.
                </div>
            ) : (
                <div className="bt-field">
                    <label className="bt-field-label">Photo</label>
                    {photo ? (
                        <div className="bt-photo-preview">
                            <img src={photo.dataUrl} alt="Selected" />
                            <div className="bt-photo-preview-meta">
                                <span>{photo.width}×{photo.height}</span>
                                <span>{Math.round(photo.bytes / 1024)} KB</span>
                                <button
                                    type="button"
                                    className="bt-quote-btn remove"
                                    onClick={() => setPhoto(null)}
                                >
                                    Remove
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="bt-photo-picker"
                            onClick={() => fileRef.current && fileRef.current.click()}
                            disabled={busy}
                        >
                            <span role="img" aria-label="Camera">📷</span>
                            {busy ? ' Processing…' : ' Choose or take a photo'}
                        </button>
                    )}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFile}
                        style={{ display: 'none' }}
                    />
                    <div className="bt-field-hint">
                        Images are resized and compressed on this device before saving.
                        You can also save a description with no photo.
                    </div>
                </div>
            )}

            <div className="bt-field">
                <label className="bt-field-label">Description *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="e.g. Before repair — leaking pipe under sink"
                />
                {error && <div className="bt-field-error">{error}</div>}
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="bt-btn bt-btn-save"
                    onClick={submit}
                    disabled={busy}
                >
                    {photo ? 'Save Photo' : 'Save Entry'}
                </button>
            </div>
        </div>
    );
}

function CompleteForm({ defaultValue, onCancel, onSave }) {
    const [cost, setCost] = useState(defaultValue || '');
    const [error, setError] = useState('');
    const submit = () => {
        if (!cost.trim()) {
            setError('Enter the final cost to close this order.');
            return;
        }
        onSave(cost);
    };
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Mark order completed</div>
            <div className="bt-field">
                <label className="bt-field-label">Final / Actual Cost *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    placeholder="e.g. $720"
                />
                {error && <div className="bt-field-error">{error}</div>}
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="bt-btn bt-btn-save" onClick={submit}>
                    Mark Completed
                </button>
            </div>
        </div>
    );
}

export default MaintenanceView;
